import { useState, useRef } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/common";
import { ClipboardCheck, Plus, Send, CheckCircle2, XCircle, BarChart3, Lock, Trash2, BellRing, Upload } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  utkast: { tk: "st_utkast", v: "secondary" },
  vantar_godkannande: { tk: "st_vantar", v: "outline" },
  publicerad: { tk: "st_publicerad", v: "default" },
  stangd: { tk: "st_stangd", v: "secondary" },
};
const QTYPES = [
  { v: "text", tk: "qt_text" }, { v: "single", tk: "qt_single" },
  { v: "multi", tk: "qt_multi" }, { v: "scale", tk: "qt_scale" },
];

function QuestionBuilder({ questions, setQuestions }) {
  const { t } = useLang();
  const add = () => setQuestions((q) => [...q, { text: "", type: "text", options: "" }]);
  const upd = (i, patch) => setQuestions((q) => q.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const del = (i) => setQuestions((q) => q.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {questions.map((q, i) => (
        <div key={i} className="rounded-md border p-3" data-testid={`qbuild-${i}`}>
          <div className="flex gap-2">
            <Input placeholder={t("q_num", { n: i + 1 })} value={q.text} onChange={(e) => upd(i, { text: e.target.value })} data-testid={`q-text-${i}`} />
            <Select value={q.type} onValueChange={(v) => upd(i, { type: v })}>
              <SelectTrigger className="w-36" data-testid={`q-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{QTYPES.map((ty) => <SelectItem key={ty.v} value={ty.v}>{t(ty.tk)}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="icon" variant="ghost" onClick={() => del(i)} data-testid={`q-del-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
          {(q.type === "single" || q.type === "multi") &&
            <Input className="mt-2" placeholder={t("q_opts_ph")} value={q.options} onChange={(e) => upd(i, { options: e.target.value })} data-testid={`q-opts-${i}`} />}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="q-add"><Plus className="mr-1 h-4 w-4" />{t("q_add")}</Button>
    </div>
  );
}

function Bars({ counts }) {
  const { t } = useLang();
  const entries = Object.entries(counts || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">{t("no_answers")}</p>;
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-sm">
          <span className="w-24 shrink-0 truncate">{k}</span>
          <div className="h-4 flex-1 overflow-hidden rounded bg-muted">
            <div className="h-full rounded bg-accent" style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <span className="w-8 text-right font-mono text-xs">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function Enkater() {
  const { t } = useLang();
  const { effectiveRole } = useAuth();
  const canApprove = ["owner", "hr_admin", "chef"].includes(effectiveRole);
  const { data: surveys, reload } = useApi("/surveys");
  const { data: teams } = useApi("/teams");
  const { data: departments } = useApi("/departments");
  const { data: businessAreas } = useApi("/business_areas");
  const { data: shifts } = useApi("/shifts");
  const [form, setForm] = useState({ title: "", description: "", audienceType: "all", audienceId: "" });
  const [questions, setQuestions] = useState([{ text: "", type: "text", options: "" }]);
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      let title = form.title, description = form.description, qs = [];
      if (f.name.toLowerCase().endsWith(".json")) {
        const data = JSON.parse(text);
        title = data.title || title || f.name.replace(/\.json$/i, "");
        description = data.description || description;
        qs = (data.questions || []).map((q) => ({
          text: q.text || q.question || "",
          type: ["text", "single", "multi", "scale"].includes(q.type) ? q.type : "text",
          options: Array.isArray(q.options) ? q.options.join(", ") : (q.options || ""),
        }));
      } else {
        qs = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
          const [tx, ty, op] = l.split("|").map((x) => (x || "").trim());
          return { text: tx, type: ["text", "single", "multi", "scale"].includes(ty) ? ty : "text", options: (op || "").split(";").map((s) => s.trim()).filter(Boolean).join(", ") };
        });
        if (!title) title = f.name.replace(/\.(csv|txt)$/i, "");
      }
      qs = qs.filter((q) => q.text);
      if (!qs.length) return toast.error("Hittade inga frågor i filen");
      setForm((fm) => ({ ...fm, title, description }));
      setQuestions(qs);
      toast.success(`Importerade ${qs.length} frågor`);
    } catch (err) { toast.error("Kunde inte läsa filen – använd JSON eller CSV (en fråga per rad)"); }
    finally { if (e.target) e.target.value = ""; }
  };

  const create = async (submit) => {
    if (!form.title.trim()) return toast.error(t("sv_enter_title"));
    const qs = questions.filter((q) => q.text.trim()).map((q, i) => ({
      id: `q${i + 1}`, text: q.text.trim(), type: q.type,
      options: (q.type === "single" || q.type === "multi") ? q.options.split(",").map((o) => o.trim()).filter(Boolean) : [],
    }));
    if (qs.length === 0) return toast.error(t("sv_add_q"));
    const audience = form.audienceType === "all" ? { type: "all" } : { type: form.audienceType, ids: form.audienceId ? [form.audienceId] : [] };
    try {
      await api.post("/surveys", { title: form.title, description: form.description, questions: qs, audience, submit_for_approval: submit });
      toast.success(submit ? t("sv_sent_approval") : t("sv_draft_saved"));
      setForm({ title: "", description: "", audienceType: "all", audienceId: "" });
      setQuestions([{ text: "", type: "text", options: "" }]);
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const act = async (id, path, ok) => { try { await api.post(`/surveys/${id}/${path}`); toast.success(ok); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const openResults = async (id) => { try { const r = await api.get(`/surveys/${id}/results`); setResults(r.data); } catch (e) { toast.error(apiErr(e)); } };
  const remind = async (id) => { try { const r = await api.post(`/surveys/${id}/remind`); toast.success(t("sv_remind_sent", { n: r.data.reminded })); } catch (e) { toast.error(apiErr(e)); } };
  const opts = form.audienceType === "business_area" ? businessAreas : form.audienceType === "department" ? departments : form.audienceType === "team" ? teams : form.audienceType === "shift" ? shifts : [];

  return (
    <div>
      <PageHeader title={t("sv_title")} icon={ClipboardCheck} subtitle={t("sv_sub")} />

      <Card className="mb-6 grid gap-3 p-4 lg:grid-cols-2">
        <div className="lg:col-span-2"><Label>{t("sv_title_lbl")}</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="survey-title" /></div>
        <div className="lg:col-span-2"><Label>{t("sv_desc_lbl")}</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="survey-desc" /></div>
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <Label>{t("sv_questions")}</Label>
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-secondary" data-testid="survey-import-btn">
              <Upload className="h-3.5 w-3.5" />Importera färdig enkät
              <input ref={fileRef} type="file" accept=".json,.csv,.txt" className="hidden" onChange={onImport} data-testid="survey-import-input" />
            </label>
          </div>
          <QuestionBuilder questions={questions} setQuestions={setQuestions} />
        </div>
        <div><Label>{t("sv_audience")}</Label>
          <Select value={form.audienceType} onValueChange={(v) => setForm((f) => ({ ...f, audienceType: v, audienceId: "" }))}>
            <SelectTrigger data-testid="survey-aud-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("aud_all")}</SelectItem><SelectItem value="business_area">{t("aud_area")}</SelectItem><SelectItem value="department">{t("aud_dept")}</SelectItem><SelectItem value="team">{t("aud_team")}</SelectItem><SelectItem value="shift">{t("aud_shift")}</SelectItem></SelectContent>
          </Select></div>
        {form.audienceType !== "all" &&
          <div><Label>{t("choose")}</Label>
            <Select value={form.audienceId} onValueChange={(v) => setForm((f) => ({ ...f, audienceId: v }))}>
              <SelectTrigger data-testid="survey-aud-target"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select></div>}
        <div className="flex gap-2 lg:col-span-2">
          <Button variant="outline" onClick={() => create(false)} data-testid="survey-save-draft"><Plus className="mr-1 h-4 w-4" />{t("sv_save_draft")}</Button>
          <Button onClick={() => create(true)} data-testid="survey-submit"><Send className="mr-1 h-4 w-4" />{t("sv_submit")}</Button>
        </div>
      </Card>

      {(surveys || []).length === 0 ? <EmptyState icon={ClipboardCheck} title={t("sv_empty_title")} text={t("sv_empty_text")} /> :
        <div className="space-y-2">
          {surveys.map((s) => (
            <Card key={s.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`survey-${s.id}`}>
              <div className="min-w-[180px] flex-1">
                <div className="font-medium">{s.title}</div>
                <div className="text-xs text-muted-foreground">{t("sv_n_questions", { n: (s.questions || []).length })} · {s.audience?.type || "all"}</div>
              </div>
              <Badge variant={STATUS[s.status]?.v || "secondary"}>{STATUS[s.status] ? t(STATUS[s.status].tk) : s.status}</Badge>
              {s.status === "utkast" && <Button size="sm" variant="outline" onClick={() => act(s.id, "submit", t("sv_sent"))} data-testid={`submit-${s.id}`}>{t("sv_to_approval")}</Button>}
              {s.status === "vantar_godkannande" && canApprove && <>
                <Button size="sm" onClick={() => act(s.id, "approve", t("st_publicerad"))} data-testid={`approve-${s.id}`}><CheckCircle2 className="mr-1 h-4 w-4" />{t("sv_approve")}</Button>
                <Button size="sm" variant="ghost" onClick={() => act(s.id, "reject", t("sv_reverted"))} data-testid={`reject-${s.id}`}><XCircle className="mr-1 h-4 w-4" />{t("sv_reject")}</Button>
              </>}
              {s.status === "vantar_godkannande" && !canApprove && <Badge variant="outline" className="gap-1"><Lock className="h-3 w-3" />{t("sv_awaiting_hr")}</Badge>}
              {s.status === "publicerad" && <>
                <Button size="sm" variant="outline" onClick={() => openResults(s.id)} data-testid={`results-${s.id}`}><BarChart3 className="mr-1 h-4 w-4" />{t("sv_results")}</Button>
                <Button size="sm" variant="ghost" onClick={() => remind(s.id)} data-testid={`remind-${s.id}`}><BellRing className="mr-1 h-4 w-4" />{t("sv_remind")}</Button>
                {canApprove && <Button size="sm" variant="ghost" onClick={() => act(s.id, "close", t("sv_closed"))}>{t("close")}</Button>}
              </>}
            </Card>
          ))}
        </div>}

      <Dialog open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("sv_results_label")}: {results?.survey?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("sv_n_answers", { n: results?.response_count || 0 })}</p>
          <div className="max-h-[55vh] space-y-3 overflow-y-auto">
            {(results?.survey?.questions || []).map((q) => {
              const agg = results.aggregate?.[q.id] || {};
              return (
                <div key={q.id} className="rounded-md border p-3">
                  <div className="mb-2 text-sm font-medium">{q.text}</div>
                  {agg.type === "text" ? (
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {(agg.answers || []).length === 0 ? <li>{t("no_answers")}</li> : agg.answers.map((a, i) => <li key={i}>• {a}</li>)}
                    </ul>
                  ) : <Bars counts={agg.counts} />}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
